import { useState, useCallback, useRef } from 'react';
import { getChatCompletion } from '../services/aiIntegrations/chatCompletion';

/**
 * Unified React hook for chat functionality
 * @param {string} provider - AI provider
 * @param {string} model - Model name
 * @param {boolean} streaming - Enable streaming (not used in Lambda mode)
 * @returns {{ response, fullResponse, isLoading, error, sendMessage, clearError, cancel }}
 */
export function useChat(provider = 'OPEN_AI', model = 'gpt-4o', streaming = false) {
  const [response, setResponse] = useState(null);
  const [fullResponse, setFullResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(false);

  const clearError = useCallback(() => setError(null), []);

  const sendMessage = useCallback(
    async (messages, options = {}) => {
      abortRef.current = false;
      setIsLoading(true);
      setError(null);
      setResponse(null);
      setFullResponse(null);

      try {
        const result = await getChatCompletion(provider, model, messages, options);
        if (abortRef?.current) return null;

        setFullResponse(result);
        const content = result?.choices?.[0]?.message?.content || result?.content || '';
        setResponse(content);
        return result;
      } catch (err) {
        if (abortRef?.current) return null;
        const errorMessage = err?.message || 'AI service request failed';
        setError(new Error(errorMessage));
        return null;
      } finally {
        if (!abortRef?.current) {
          setIsLoading(false);
        }
      }
    },
    [provider, model]
  );

  const cancel = useCallback(() => {
    abortRef.current = true;
    setIsLoading(false);
  }, []);

  return { response, fullResponse, isLoading, error, sendMessage, clearError, cancel };
}

export default useChat;
